/**
 * P3: Stripe Service - Frontend Abstraction Layer
 * ================================================
 *
 * All actual payment processing happens on the backend.
 * Frontend only calls backend API via axios.
 */

import api from '../utils/api';

/**
 * TreatX Package IDs
 */
export const PACKAGE_IDS = {
  TRIAL: 'TRIAL',
  STARTER: 'STARTER',
  PRO: 'PRO',
  ENTERPRISE: 'ENTERPRISE',
};

/**
 * Subscription Status enum
 */
export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'ACTIVE',
  TRIALING: 'TRIALING',
  PAST_DUE: 'PAST_DUE',
  CANCELED: 'CANCELED',
  PENDING: 'PENDING',
  EXPIRED: 'EXPIRED',
  UNPAID: 'UNPAID',
};

/**
 * Stripe Service - Frontend API Client
 */
const StripeService = {
  async getAvailablePackages() {
    const response = await api.get(`/subscription/packages`);
    return response.data;
  },

  async createCheckoutSession({ packageId, clinicId, clinicEmail, clinicName = '' }) {
    // P0-014 FIX: Try upgrade endpoint first (for clinics with existing subscriptions)
    // then fall back to checkout/session for fresh subscriptions
    try {
      const upgradeRes = await api.post(`/subscription/clinic/${clinicId}/upgrade`, {
        new_package_key: packageId,
        clinic_email: clinicEmail,
        origin_url: window.location.origin,
      });
      const data = upgradeRes.data;
      return {
        checkoutUrl: data.checkout_url,
        sessionId: data.session_id,
        packageId: data.package_id || packageId,
        // ZERO-PRICE-UPGRADE-01 — `||` turned a valid 0 into undefined.
        amount: data.new_amount ?? data.amount,
        currency: data.currency,
        packageName: data.package_name,
        // Explicit backend confirmation that the upgrade is already active
        // and that no PSP interaction is required. The caller must never
        // infer success from a merely absent checkout_url.
        activated: data.activated,
        paymentRequired: data.payment_required,
        zeroPrice: data.zero_price,
      };
    } catch (upgradeErr) {
      const errDetail = upgradeErr.response?.data?.detail || upgradeErr.message || '';
      const isNoSubscription = errDetail.toLowerCase().includes('no active subscription');
      if (!isNoSubscription) {
        // Rethrow non-subscription errors (e.g., "Cannot upgrade from X to Y", "not active")
        throw new Error(errDetail || 'Upgrade failed');
      }
    }

    // Fallback: fresh checkout (no existing subscription)
    const response = await api.post(`/subscription/checkout/session`, {
      package_id: packageId,
      clinic_id: clinicId,
      clinic_email: clinicEmail,
      clinic_name: clinicName,
      origin_url: window.location.origin,
    }).catch(err => {
      const detail = err.response?.data?.detail;
      if (detail?.reason === 'no_stripe_price') {
        throw new Error(detail.message || 'This package does not have a payment price configured.');
      }
      throw err;
    });
    const data = response.data;
    return {
      checkoutUrl: data.checkout_url,
      sessionId: data.session_id,
      packageId: data.package_id,
      amount: data.amount,
      currency: data.currency,
      packageName: data.package_name,
      // TREATX-IYZICO-P0F3-B — an EMBEDDED provider returns documented form
      // markup instead of a hosted URL. Neither one is evidence of payment:
      // confirmation always comes from the verified backend status.
      provider: data.provider,
      formContent: data.form_content,
      tokenExpiresIn: data.token_expires_in,
      payableAmount: data.payable_amount,
      taxAmount: data.tax_amount,
    };
  },

  async getCheckoutStatus(sessionId) {
    const response = await api.get(`/subscription/checkout/status/${sessionId}`);
    const data = response.data;
    return {
      sessionId: data.session_id,
      status: data.status,
      paymentStatus: data.payment_status,
      amountTotal: data.amount_total,
      currency: data.currency,
      clinicId: data.clinic_id,
      packageId: data.package_id,
    };
  },

  async getClinicSubscription(clinicId) {
    const response = await api.get(`/subscription/clinic/${clinicId}`);
    const data = response.data;
    return {
      hasSubscription: data.has_subscription,
      subscriptionId: data.subscription_id,
      clinicId: data.clinic_id,
      packageId: data.package_id,
      packageName: data.package_name,
      status: data.status,
      amount: data.amount,
      currency: data.currency,
      createdAt: data.created_at,
      message: data.message,
    };
  },

  async cancelSubscription(clinicId, atPeriodEnd = true) {
    const response = await api.post(`/subscription/clinic/${clinicId}/cancel`, {
      at_period_end: atPeriodEnd,
    });
    return response.data;
  },

  async reconcileSession(sessionId) {
    const response = await api.post(`/subscription/reconcile/${sessionId}`);
    return response.data;
  },

  async requestEnterprisePlan({ clinicId, clinicEmail, clinicName, message = '' }) {
    const response = await api.post(`/subscription/enterprise-request`, {
      clinic_id: clinicId,
      clinic_email: clinicEmail,
      clinic_name: clinicName,
      message,
    });
    return response.data;
  },

  async upgradeSubscription({ clinicId, newPackageId, clinicEmail }) {
    const response = await api.post(`/subscription/clinic/${clinicId}/upgrade`, {
      new_package_key: newPackageId,
      clinic_email: clinicEmail,
      origin_url: window.location.origin,
    });
    const data = response.data;
    return {
      success: data.success,
      action: data.action,
      checkoutUrl: data.checkout_url,
      sessionId: data.session_id,
      fromPackage: data.from_package,
      toPackage: data.to_package,
      amount: data.amount,
      message: data.message,
    };
  },

  async downgradeSubscription({ clinicId, newPackageId }) {
    const response = await api.post(`/subscription/clinic/${clinicId}/downgrade`, {
      new_package_key: newPackageId,
    });
    const data = response.data;
    return {
      success: data.success,
      clinicId: data.clinic_id,
      currentPackage: data.current_package,
      downgradeTo: data.downgrade_to,
      downgradeToName: data.downgrade_to_name,
      newAmount: data.new_amount,
      status: data.status,
      message: data.message,
    };
  },

  async cancelPendingDowngrade(clinicId) {
    const response = await api.post(`/subscription/clinic/${clinicId}/cancel-downgrade`);
    return response.data;
  },

  isContactSalesRequired(packageId) {
    return packageId?.toUpperCase() === 'ENTERPRISE';
  },

  async redirectToCheckout({ packageId, clinicId, clinicEmail }) {
    if (this.isContactSalesRequired(packageId)) {
      throw new Error('Enterprise plan requires Contact Sales flow');
    }
    const checkout = await this.createCheckoutSession({ packageId, clinicId, clinicEmail });
    window.location.href = checkout.checkoutUrl;
  },

  async pollCheckoutStatus(sessionId, maxAttempts = 30, intervalMs = 2000) {
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const status = await this.getCheckoutStatus(sessionId);
      if (status.paymentStatus === 'paid' || status.status === 'complete') {
        return status;
      }
      if (status.status === 'expired') {
        throw new Error('Checkout session expired');
      }
      await new Promise(resolve => setTimeout(resolve, intervalMs));
    }
    throw new Error('Polling timeout - payment status unknown');
  },
};

export default StripeService;
